import Link from 'next/link';
import React from 'react';

import classNames from './Pager.module.scss';

const AnchorOrDiv: React.FC<
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
    className?: string;
    href?: string;
    children: React.ReactNode;
    active?: boolean;
  }
> = ({ className, href, children, active, ...rest }) => {
  if (href) {
    return (
      <Link className={className} href={href} data-active={active} {...rest}>
        {children}
      </Link>
    );
  }
  return (
    <div className={className} aria-disabled="true" data-active={active}>
      {children}
    </div>
  );
};

export const Pager: React.FC<{
  className?: string;
  numbers: {
    value: number;
    href?: string;
    active?: boolean;
  }[];
  prev?: {
    href?: string;
  };
  next?: {
    href?: string;
  };
}> = ({ className, prev, numbers, next }) => {
  return (
    <nav className={`${className} ${classNames.pager}`}>
      {prev && (
        <AnchorOrDiv className={classNames['pager-left']} href={prev.href}>
          <svg className={classNames['pager-left-circle']}>
            <rect
              className={classNames['pager-left-circle-rect']}
              rx={100}
              ry={100}
              x={0}
              y={0}
              width={60}
              height={60}
            />
            <linearGradient
              id="pager-left-linear-gradient"
              spreadMethod="pad"
              x1="0.155"
              x2="0.935"
              y1="0.741"
              y2={0}
            >
              <stop offset={0} stopColor="#007fd3" stopOpacity={1} />
              <stop offset={1} stopColor="#279be8" stopOpacity={1} />
            </linearGradient>
            <linearGradient
              id="pager-left-hover-linear-gradient"
              spreadMethod="pad"
              x1="0.155"
              x2="0.935"
              y1="0.741"
              y2={0}
            >
              <stop offset={0} stopColor="#0e89da" stopOpacity={1} />
              <stop offset={1} stopColor="#7ccafd" stopOpacity={1} />
            </linearGradient>
          </svg>
          <svg
            className={classNames['pager-left-icon']}
            viewBox="-11070.826 -5880.902 13.818 3.336"
          >
            <path
              className={classNames['pager-left-icon-path']}
              d="M -11057.0078125 -5877.56640625 L -11070.826171875 -5877.56640625 L -11067.4677734375 -5880.90234375"
            />
          </svg>
        </AnchorOrDiv>
      )}
      <div className={classNames['pager-numbers']}>
        {numbers.map((number) => {
          return (
            <AnchorOrDiv
              key={number.value}
              className={classNames['pager-number']}
              href={number.href}
              active={number.active}
            >
              {number.value}
            </AnchorOrDiv>
          );
        })}
      </div>
      {next && (
        <AnchorOrDiv className={classNames['pager-right']} href={next.href}>
          <svg className={classNames['pager-right-circle']}>
            <rect
              className={classNames['pager-right-circle-rect']}
              rx={100}
              ry={100}
              x={0}
              y={0}
              width={60}
              height={60}
            />
            <linearGradient
              id="pager-right-linear-gradient"
              spreadMethod="pad"
              x1="0.155"
              x2="0.935"
              y1="0.741"
              y2={0}
            >
              <stop offset={0} stopColor="#007fd3" stopOpacity={1} />
              <stop offset={1} stopColor="#279be8" stopOpacity={1} />
            </linearGradient>
            <linearGradient
              id="pager-right-hover-linear-gradient"
              spreadMethod="pad"
              x1="0.155"
              x2="0.935"
              y1="0.741"
              y2={0}
            >
              <stop offset={0} stopColor="#0e89da" stopOpacity={1} />
              <stop offset={1} stopColor="#7ccafd" stopOpacity={1} />
            </linearGradient>
          </svg>
          <svg
            className={classNames['pager-right-icon']}
            viewBox="-11070.826 -5880.902 13.818 3.336"
          >
            <path
              className={classNames['pager-right-icon-path']}
              d="M -11070.826171875 -5877.56640625 L -11057.0078125 -5877.56640625 L -11060.3662109375 -5880.90234375"
            />
          </svg>
        </AnchorOrDiv>
      )}
    </nav>
  );
};
